import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Landmark } from "lucide-react";

function rupee(n) { return `₹${Number(n).toLocaleString("en-IN")}`; }

export default function Checkout() {
  const { items, subtotal, clear, fetchCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [placed, setPlaced] = useState(null);
  const [loading, setLoading] = useState(false);
  const [methods, setMethods] = useState({ stripe: false, razorpay: false, bank_transfer: false, bank_details: [] });
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    payment_method: "razorpay",
  });

  // Fetch which payment methods are enabled on backend.
  useEffect(() => {
    api.get("/payments/methods").then(({ data }) => {
      setMethods(data);
      setForm((f) => ({
        ...f,
        payment_method: data.razorpay ? "razorpay" : data.bank_transfer ? "bank_transfer" : "",
      }));
    }).catch(() => {});
  }, []);

  const shipping = subtotal >= 5000 ? 0 : 200;
  const total = subtotal + shipping;

  if (items.length === 0 && !placed) {
    return (
      <div className="px-6 md:px-24 py-24 text-center">
        <div className="font-serif-h text-3xl">Your bag is empty</div>
        <Link to="/shop/sarees" className="btn-ink mt-6 inline-flex">Continue Shopping</Link>
      </div>
    );
  }

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const openRazorpay = async (order, config) => {
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast.error("Could not load Razorpay. Check your internet.");
      return;
    }
    return new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key: config.key_id,
        amount: config.amount_paise,
        currency: config.currency,
        name: "Riddhi — Drape the Elegance",
        description: `Order #${order.id.slice(0, 8).toUpperCase()}`,
        order_id: config.razorpay_order_id,
        prefill: config.prefill,
        theme: { color: "#5A1618" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            resolve({ ok: true });
          } catch (err) {
            toast.error("Payment verification failed. Please contact us.");
            resolve({ ok: false });
          }
        },
        modal: { ondismiss: () => resolve({ ok: false, cancelled: true }) },
      });
      rzp.open();
    });
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Guests must send their cart items in the body — signed-in users use
      // the server cart (backend ignores body.items when authenticated).
      const body = user
        ? form
        : { ...form, items: items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })) };
      const { data: order } = await api.post("/orders", body);

      if (form.payment_method === "stripe") {
        // 2. Ask backend to create a Stripe checkout session.
        const { data: sess } = await api.post("/payments/stripe/checkout", {
          order_id: order.id,
          origin_url: window.location.origin,
        });
        // 3. Redirect to Stripe-hosted page.
        window.location.href = sess.url;
        return;
      }

      if (form.payment_method === "razorpay") {
        const { data: cfg } = await api.post("/payments/razorpay/create-order", { order_id: order.id });
        const result = await openRazorpay(order, cfg);
        if (result?.ok) {
          clear();
          fetchCart();
          navigate(`/orders/${order.id}?paid=1`);
        } else if (result?.cancelled) {
          toast("Payment cancelled — you can retry from your orders page.");
          clear();
          fetchCart();
          navigate(`/orders/${order.id}`);
        }
        return;
      }

      // Bank transfer / non-redirect flows → order is instantly placed.
      setPlaced(order);
      clear();
      fetchCart();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not place order");
    } finally {
      setLoading(false);
    }
  };

  if (placed) {
    return (
      <div className="px-6 md:px-24 py-24 text-center" data-testid="order-confirmation">
        <CheckCircle2 size={56} strokeWidth={1} className="mx-auto text-[#D4AF37]" />
        <div className="font-serif-h text-4xl mt-6">Order placed with care</div>
        <div className="text-stone-600 mt-3">Order #{placed.id.slice(0, 8).toUpperCase()}</div>

        {placed.payment_method === "bank_transfer" && methods.bank_details?.length > 0 && (
          <div className="mt-8 max-w-lg mx-auto text-left border border-warm p-6 bg-[#FDFBF7]" data-testid="bank-transfer-details">
            <div className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-3">Bank Transfer Details</div>
            <div className="font-serif-h text-xl mb-4">Please transfer {rupee(placed.total)} to:</div>
            <ul className="text-sm text-stone-700 space-y-1">
              {methods.bank_details.map((line) => <li key={line}>{line}</li>)}
            </ul>
            <div className="text-xs text-stone-500 mt-4 leading-relaxed">
              Reference: <b>#{placed.id.slice(0, 8).toUpperCase()}</b>. Once transferred, WhatsApp your payment screenshot to
              <a href="https://wa.me/918660515091" target="_blank" rel="noreferrer" className="text-[#25D366] font-medium ml-1">+91 86605 15091</a>.
            </div>
          </div>
        )}

        <p className="text-stone-600 mt-6 max-w-md mx-auto">
          We'll send a confirmation to <b>{placed.shipping_address.email}</b>. Your pieces will ship within 3–5 business days.
        </p>
        <div className="flex gap-4 justify-center mt-10">
          <Link to="/orders" className="btn-ink">View My Orders</Link>
          <Link to="/shop/sarees" className="btn-gold-outline">Keep Shopping</Link>
        </div>
      </div>
    );
  }

  // Assemble payment options dynamically from backend config.
  const paymentOptions = [
    methods.razorpay && { v: "razorpay", t: "UPI / Cards / Netbanking / Wallets", d: "All Indian payment methods — secured by Razorpay.", icon: CreditCard },
    methods.bank_transfer && { v: "bank_transfer", t: "Bank Transfer / UPI", d: "We'll share account details on the next step.", icon: Landmark },
  ].filter(Boolean);

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-3">Secure Checkout</div>
      <h1 className="font-serif-h font-light text-5xl text-stone-900">Checkout</h1>

      <form onSubmit={placeOrder} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-10">
          <section>
            <h2 className="font-serif-h text-2xl mb-6">Shipping Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Full Name</label><input required className="input-line" value={form.full_name} onChange={onChange("full_name")} data-testid="co-name" /></div>
              <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Email</label><input required type="email" className="input-line" value={form.email} onChange={onChange("email")} data-testid="co-email" /></div>
              <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Phone</label><input required className="input-line" value={form.phone} onChange={onChange("phone")} data-testid="co-phone" /></div>
              <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Pincode</label><input required className="input-line" value={form.pincode} onChange={onChange("pincode")} data-testid="co-pincode" /></div>
              <div className="md:col-span-2"><label className="text-[11px] tracking-overline uppercase text-stone-500">Address Line 1</label><input required className="input-line" value={form.address_line1} onChange={onChange("address_line1")} data-testid="co-addr1" /></div>
              <div className="md:col-span-2"><label className="text-[11px] tracking-overline uppercase text-stone-500">Address Line 2 (optional)</label><input className="input-line" value={form.address_line2} onChange={onChange("address_line2")} /></div>
              <div><label className="text-[11px] tracking-overline uppercase text-stone-500">City</label><input required className="input-line" value={form.city} onChange={onChange("city")} data-testid="co-city" /></div>
              <div><label className="text-[11px] tracking-overline uppercase text-stone-500">State</label><input required className="input-line" value={form.state} onChange={onChange("state")} data-testid="co-state" /></div>
            </div>
          </section>

          <section>
            <h2 className="font-serif-h text-2xl mb-6">Payment Method</h2>
            <div className="space-y-3" data-testid="payment-methods">
              {paymentOptions.map((p) => {
                const Icon = p.icon;
                const selected = form.payment_method === p.v;
                return (
                  <label
                    key={p.v}
                    className={`flex items-start gap-4 p-5 border cursor-pointer transition ${selected ? "border-[#D4AF37] bg-[#F8EDE9]/50" : "border-warm hover:border-[#D4AF37]/60"}`}
                  >
                    <input
                      type="radio"
                      name="pay"
                      checked={selected}
                      onChange={() => setForm({ ...form, payment_method: p.v })}
                      data-testid={`pay-${p.v}`}
                      className="mt-1 accent-[#5A1618]"
                    />
                    <Icon size={20} strokeWidth={1.3} className={`mt-0.5 ${selected ? "text-[#D4AF37]" : "text-stone-500"}`} />
                    <div className="flex-1">
                      <div className="font-medium text-stone-900 text-sm">{p.t}</div>
                      <div className="text-xs text-stone-500 mt-1">{p.d}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {paymentOptions.length === 0 && (
              <div className="text-sm text-stone-500">No payment methods available. Please contact support.</div>
            )}
          </section>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-warm p-8 bg-white sticky top-28">
            <div className="font-serif-h text-2xl mb-5">Your Order</div>
            <div className="space-y-4 max-h-[380px] overflow-y-auto pr-2">
              {items.map((it) => (
                <div key={`${it.product_id}-${it.size}`} className="flex gap-3">
                  <img src={it.product?.images?.[0]} className="w-16 h-20 object-cover bg-[#F3EAE8]" alt="" />
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{it.product?.name}</div>
                    <div className="text-xs text-stone-500">Size {it.size} · Qty {it.quantity}</div>
                  </div>
                  <div className="text-sm">{rupee((it.product?.price || 0) * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="gold-divider my-5" />
            <div className="flex justify-between text-sm"><span className="text-stone-600">Subtotal</span><span>{rupee(subtotal)}</span></div>
            <div className="flex justify-between text-sm mt-2"><span className="text-stone-600">Shipping</span><span>{shipping === 0 ? "Complimentary" : rupee(shipping)}</span></div>
            <div className="flex justify-between text-lg mt-4 pt-4 border-t border-warm"><span>Total</span><span className="font-medium">{rupee(total)}</span></div>
            <button
              type="submit"
              disabled={loading || paymentOptions.length === 0}
              className="btn-ink w-full justify-center mt-8 disabled:opacity-50"
              data-testid="place-order-btn"
            >
              {loading
                ? "Processing..."
                : form.payment_method === "stripe" ? "Pay with Stripe"
                : form.payment_method === "razorpay" ? "Pay with Razorpay"
                : form.payment_method === "bank_transfer" ? "Place Order & View Bank Details"
                : "Place Order"}
            </button>
            <div className="text-[10px] tracking-overline uppercase text-stone-400 mt-4 text-center">
              Secure · SSL Encrypted · No data stored on our servers
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
